import React, { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  User as UserIcon,
  PlusCircle,
  Search,
  Settings,
  LogOut,
  Edit,
  Save,
  MapPin,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Button from "./UI/Button";
import { api } from "../services/api";
import { DynamicPage, SiteContent } from "../types";
import EditableContent from "./Admin/EditableContent";

const DEFAULT_USER_AVATAR = "https://tbiz.ir/profile.png";

const MainLayout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dynamicPages, setDynamicPages] = useState<DynamicPage[]>([]);
  const [siteContent, setSiteContent] = useState<SiteContent | null>(null);

  // Footer Editing State
  const [isEditingFooter, setIsEditingFooter] = useState(false);
  const [savingFooter, setSavingFooter] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const pages = await api.getAllPages();
      setDynamicPages(pages);
      const content = await api.getPageContent();
      setSiteContent(content);
    };
    loadData();
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsMobileMenuOpen(false);
  };

  const handleSaveFooter = async () => {
    if (siteContent) {
      setSavingFooter(true);
      await api.updatePageContent(siteContent);
      setSavingFooter(false);
      setIsEditingFooter(false);
    }
  };

  const updateContactInfo = (
    key: keyof SiteContent["contact"],
    value: string,
  ) => {
    if (siteContent) {
      setSiteContent({
        ...siteContent,
        contact: {
          ...siteContent.contact,
          [key]: value,
        },
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Sticky Header */}
      <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center text-blue-900 font-bold text-2xl">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L2 22H22L12 2Z"
                      stroke="#1e3a8a"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path d="M12 10L7 20H17L12 10Z" fill="#f97316" />
                  </svg>
                </div>
                <span className="text-2xl font-black text-[#1e3a5f] tracking-tight">
                  TBIZ
                </span>
              </Link>
            </div>

            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-gray-700">
              <Link
                to="/"
                className="text-orange-500 hover:text-orange-600 transition-colors"
              >
                خانه
              </Link>
              <Link
                to="/listings"
                className={`hover:text-orange-500 transition-colors ${location.pathname === '/listings' ? 'text-orange-500' : ''}`}
              >
                آگهی‌ها
              </Link>
              <Link 
                to="/services" 
                className={`hover:text-orange-500 transition-colors ${location.pathname === '/services' ? 'text-orange-500' : ''}`}
              >
                خدمات
              </Link>
              <Link 
                to="/valuation" 
                className={`hover:text-orange-500 transition-colors ${location.pathname === '/valuation' ? 'text-orange-500' : ''}`}
              >
                ارزش‌گذاری
              </Link>
              <Link 
                to="/articles" 
                className={`hover:text-orange-500 transition-colors ${location.pathname === '/articles' ? 'text-orange-500' : ''}`}
              >
                مقالات
              </Link>
              <Link
                to="/about"
                className={`hover:text-orange-500 transition-colors ${location.pathname === '/about' ? 'text-orange-500' : ''}`}
              >
                درباره ما
              </Link>
              <Link
                to="/contact"
                className={`hover:text-orange-500 transition-colors ${location.pathname === '/contact' ? 'text-orange-500' : ''}`}
              >
                تماس با ما
              </Link>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-3">
                <Button
                  onClick={() => navigate("/create-listing")}
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 flex items-center gap-2"
                  size="sm"
                >
                  <PlusCircle size={18} />
                  ثبت آگهی
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/contact")}
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-4"
                >
                  تماس با ما
                </Button>
                {isAuthenticated ? (
                  <div className="flex items-center gap-3">
                    <div
                      onClick={() => navigate("/dashboard")}
                      className="flex items-center gap-2 cursor-pointer group"
                      title="داشبورد من"
                    >
                      <div className="w-9 h-9 rounded-full bg-gray-200 overflow-hidden border-2 border-transparent group-hover:border-[#1e3a5f] transition-all">
                        <img
                          src={user?.avatar || DEFAULT_USER_AVATAR}
                          alt={user?.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            if (target.src !== DEFAULT_USER_AVATAR) {
                              target.src = DEFAULT_USER_AVATAR;
                            }
                          }}
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-1 px-2 py-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all text-xs font-bold"
                      title="خروج از حساب"
                    >
                      <LogOut size={16} />
                    </button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => navigate("/login")}
                    className="bg-[#1e3a5f] hover:bg-[#152a45] text-white rounded-lg px-6"
                  >
                    ورود / ثبت نام
                  </Button>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-gray-600"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white shadow-lg animate-fadeIn">
            <div className="px-4 py-3 space-y-3">
              <Link
                to="/listings?type=sale"
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                آگهی‌های فروش
              </Link>
              <Link
                to="/listings?type=demand"
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                آگهی‌های تقاضا/خرید
              </Link>
              <Link
                to="/listings?type=investment"
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                جذب سرمایه
              </Link>
              <Link
                to="/listings?type=investor"
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                سرمایه‌گذاران
              </Link>
              <div className="border-t border-gray-100 my-2"></div>
              <Link
                to="/listings"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                آگهی‌ها
              </Link>
              <Link
                to="/services"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                خدمات
              </Link>
              <Link
                to="/valuation"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                ارزش‌گذاری
              </Link>
              <Link
                to="/articles"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                مقالات
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                درباره ما
              </Link>
              {dynamicPages.map((page) => (
                <Link
                  key={page.id}
                  to={`/pages/${page.slug}`}
                  className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-sm"
                >
                  {page.title}
                </Link>
              ))}
              <div className="border-t border-gray-100 my-2"></div>
              <Link
                to="/create-listing"
                className="block px-3 py-2 rounded-lg hover:bg-gray-50 text-teal-600 font-medium"
              >
                ثبت آگهی جدید
              </Link>
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 rounded-lg hover:bg-gray-50"
                  >
                    داشبورد من
                  </Link>
                  <Link
                    to="/profile"
                    className="block px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                  >
                    <UserIcon size={16} /> پروفایل کاربری
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-right block px-3 py-2 rounded-lg hover:bg-red-50 text-red-600 flex items-center gap-2 font-bold"
                  >
                    <LogOut size={16} /> خروج از حساب
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="block px-3 py-2 rounded-lg hover:bg-gray-50"
                >
                  ورود / ثبت نام
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#1e3a5f] text-white pt-16 pb-8 relative group/footer">
        {/* Admin Edit Button for Footer */}
        {user?.role === "admin" && (
          <div className="absolute top-4 left-4 z-10">
            {!isEditingFooter ? (
              <button
                onClick={() => setIsEditingFooter(true)}
                className="bg-white/10 p-2 rounded-full hover:bg-white/20 transition-colors text-white"
                title="ویرایش فوتر"
              >
                <Edit size={16} />
              </button>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveFooter}
                  disabled={savingFooter}
                  className="flex gap-1 items-center bg-green-600 border-none hover:bg-green-700"
                >
                  <Save size={14} /> {savingFooter ? "..." : "ذخیره"}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsEditingFooter(false)}
                  className="bg-white/10 text-white hover:bg-white/20"
                >
                  <X size={14} />
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            {/* Logo and Contact Info */}
            <div className="col-span-1 md:col-span-4">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-white font-bold text-2xl">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L2 22H22L12 2Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="round"
                    />
                    <path d="M12 10L7 20H17L12 10Z" fill="#f97316" />
                  </svg>
                </div>
                <span className="text-2xl font-black text-white tracking-tight">
                  TBIZ
                </span>
              </div>

              {siteContent && siteContent.contact && (
                <div className="space-y-4 text-sm text-gray-300">
                  <div className="flex items-start gap-3">
                    <MapPin
                      size={18}
                      className="shrink-0 mt-0.5 text-gray-400"
                    />
                    <EditableContent
                      isEditing={isEditingFooter}
                      value={siteContent.contact.address || ''}
                      onChange={(val) => updateContactInfo("address", val)}
                      className="bg-white/10 text-white border-white/20"
                      placeholder="آدرس"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0 text-gray-400"
                    >
                      <path
                        d="M21.97 18.33C21.97 18.69 21.89 19.06 21.72 19.42C21.55 19.78 21.33 20.12 21.04 20.44C20.55 20.98 20.01 21.37 19.4 21.62C18.8 21.87 18.15 22 17.45 22C16.43 22 15.34 21.76 14.19 21.27C13.04 20.78 11.89 20.12 10.75 19.29C9.6 18.45 8.51 17.52 7.47 16.49C6.44 15.45 5.51 14.36 4.68 13.22C3.86 12.08 3.2 10.94 2.72 9.81C2.24 8.67 2 7.58 2 6.54C2 5.86 2.12 5.21 2.36 4.61C2.6 4 2.98 3.44 3.51 2.94C4.15 2.31 4.85 2 5.59 2C5.87 2 6.15 2.06 6.4 2.18C6.66 2.3 6.89 2.48 7.07 2.74L9.39 6.01C9.57 6.26 9.7 6.49 9.79 6.71C9.88 6.92 9.93 7.13 9.93 7.32C9.93 7.56 9.86 7.8 9.72 8.03C9.59 8.26 9.4 8.5 9.16 8.74L8.4 9.53C8.29 9.64 8.24 9.77 8.24 9.93C8.24 10.01 8.25 10.08 8.27 10.16C8.3 10.24 8.33 10.3 8.35 10.36C8.53 10.69 8.84 11.12 9.28 11.64C9.73 12.16 10.21 12.69 10.73 13.22C11.27 13.75 11.79 14.24 12.32 14.69C12.84 15.13 13.27 15.43 13.61 15.61C13.66 15.63 13.72 15.66 13.79 15.69C13.87 15.72 13.95 15.73 14.04 15.73C14.21 15.73 14.34 15.67 14.45 15.56L15.21 14.81C15.46 14.56 15.7 14.37 15.93 14.25C16.16 14.11 16.39 14.04 16.64 14.04C16.83 14.04 17.03 14.08 17.25 14.17C17.47 14.26 17.7 14.39 17.95 14.56L21.26 16.91C21.52 17.09 21.7 17.3 21.81 17.55C21.91 17.8 21.97 18.05 21.97 18.33Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <EditableContent
                      isEditing={isEditingFooter}
                      value={siteContent.contact.phone || ''}
                      onChange={(val) => updateContactInfo("phone", val)}
                      className="bg-white/10 text-white border-white/20"
                      placeholder="تلفن"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="shrink-0 text-gray-400"
                    >
                      <path
                        d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeMiterlimit="10"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <EditableContent
                      isEditing={isEditingFooter}
                      value={siteContent.contact.email || ''}
                      onChange={(val) => updateContactInfo("email", val)}
                      className="bg-white/10 text-white border-white/20"
                      placeholder="ایمیل"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Links 1 */}
            <div className="col-span-1 md:col-span-2 md:col-start-6">
              <ul className="space-y-4 text-sm text-gray-300">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">
                    خانه
                  </Link>
                </li>
                <li>
                  <Link to="#" className="hover:text-white transition-colors">
                    مقالات
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="hover:text-white transition-colors"
                  >
                    درباره ما
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors">
                    تماس با ما
                  </Link>
                </li>
              </ul>
            </div>

            {/* Links 2 */}
            <div className="col-span-1 md:col-span-2">
              <ul className="space-y-4 text-sm text-gray-300">
                <li>
                  <Link
                    to="/listings"
                    className="hover:text-white transition-colors"
                  >
                    آگهی‌ها
                  </Link>
                </li>
                <li>
                  <Link to="#" className="hover:text-white transition-colors">
                    خدمات
                  </Link>
                </li>
                <li>
                  <Link to="#" className="hover:text-white transition-colors">
                    ارزش‌گذاری
                  </Link>
                </li>
                <li>
                  <Link to="#" className="hover:text-white transition-colors">
                    کسب‌وکار
                  </Link>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div className="col-span-1 md:col-span-3">
              <p className="text-sm text-gray-300 mb-4 leading-relaxed">
                با عضویت در خبرنامه از آخرین اخبار و مقالات مطلع شوید
              </p>
              <div className="flex items-center">
                <input
                  type="email"
                  placeholder="ایمیل خود را وارد کنید"
                  className="bg-white/10 border border-white/20 text-white text-sm rounded-r-lg px-4 py-2.5 w-full focus:outline-none focus:border-white/40 placeholder-gray-400"
                />
                <button className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-6 py-2.5 rounded-l-lg transition-colors shrink-0">
                  عضویت
                </button>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div>
              {siteContent && (
                <EditableContent
                  isEditing={isEditingFooter}
                  value={siteContent.contact.copyright}
                  onChange={(val) => updateContactInfo("copyright", val)}
                  className="bg-white/10 text-white border-white/20"
                />
              )}
              {!siteContent && "© ۱۴۰۳ تمامی حقوق برای TBIZ محفوظ است."}
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-white transition-colors">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21.5 5.5H2.5C1.67157 5.5 1 6.17157 1 7V17C1 17.8284 1.67157 18.5 2.5 18.5H21.5C22.3284 18.5 23 17.8284 23 17V7C23 6.17157 22.3284 5.5 21.5 5.5Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 7L12 14L23 7"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <a href="#" className="hover:text-white transition-colors">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 12H16"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 8V16"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
